import React, { useState, useEffect, useRef } from "react";
import { getEstimationMeetData } from '../firebase/firestoreService'; // Import the service function
import { Bar, Pie } from 'react-chartjs-2'; // Import Chart.js components
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement 
} from 'chart.js';  // Register necessary chart elements

import { useAuth } from '../context/AuthContext';
import { useNavigate } from "react-router-dom";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// Helper function to calculate percentages and differences
const calculateDemandMetPercentage = (demand, met) => {
  if (demand === 0) return 0; // Avoid division by zero
  return ((met / demand) * 100).toFixed(5); // Return percentage with two decimal points
};

const calculateNeedToGet = (demand, met) => {
  return (demand - met).toFixed(2); // Calculate the difference between demand and met
};

// Reusable StatCard Component
const StatCard = ({ title, value }) => (
  <div style={styles.statCard}>
    <div style={styles.statTitle}>{title}</div>
    <div style={styles.statValue}>{value}</div>
  </div>
);

// Reusable PieChart Component
const PieChart = ({ data, title }) => (
  <div style={styles.pieChartWrapper}>
    <Pie
      data={data}
      options={{
        responsive: true,
        plugins: {
          title: { display: false, text: false },
          legend: { display: false }, // Disable legend
        },
      }}
    />
  </div>
);

// Reusable Table Row for each crop
const CropTableRow = ({ cropData, index }) => {
  const { crop, demand, met } = cropData;
  const demandMetPercentage = calculateDemandMetPercentage(demand, met);
  const needToGet = calculateNeedToGet(demand, met);

  const cropPieChartData = {
    labels: ['Met', 'Not Met'],
    datasets: [
      {
        data: [demandMetPercentage, 100 - demandMetPercentage],
        backgroundColor: ['#27ae60', '#f39c12'], // Green for met, Orange for unmet
        hoverOffset: 4,
      },
    ],
  };

  return (
    <tr
      key={crop}
      style={index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}
      onMouseEnter={(e) => (e.target.style.backgroundColor = '#f1f1f1')}
      onMouseLeave={(e) => (e.target.style.backgroundColor = index % 2 === 0 ? '#f9f9f9' : '#ffffff')}
    >
      <td style={styles.td}>{crop}</td>
      <td style={styles.td}>{demand.toFixed(3)}</td>
      <td style={styles.td}>{met.toFixed(3)}</td>
      <td style={styles.td}>{demandMetPercentage}%</td>
      <td style={styles.td}>{needToGet}</td>
      <td style={styles.td}>
        <PieChart data={cropPieChartData} title={`${crop} - Demand Met Percentage`} />
      </td>
    </tr>
  );
};

const EstimationMeetComponent = () => {
  const [estimationMeetData, setEstimationMeetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // Default to descending order
  const [sortedData, setSortedData] = useState([]);

  /**
   * New changes: login option in demand page
   */
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, setUser } = useAuth();
  const [hoveredItem, setHoveredItem] = useState(null);
  const dropdownRef = useRef(null); // Reference for the dropdown menu
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEstimationMeetData = async () => {
      try {
        const data = await getEstimationMeetData(); // Fetch data from Firebase
        setEstimationMeetData(data);

        // Sort data based on "met" value initially in descending order
        const sorted = Object.keys(data).map((crop) => ({
          crop,
          ...data[crop],
        }));
        sorted.sort((a, b) => b.met - a.met);
        setSortedData(sorted);
      } catch (err) {
        setError(err.message); // Set error if fetching fails
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    };

    fetchEstimationMeetData();


    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMenuOpen(false); // Close the menu if clicking outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };

  }, []);

  // Sort data by "met" value
  const handleSort = () => {
    const sorted = [...sortedData];
    const order = sortOrder === 'asc' ? 'desc' : 'asc'; // Toggle between ascending and descending

    sorted.sort((a, b) => (order === 'asc' ? a.met - b.met : b.met - a.met));

    setSortOrder(order);
    setSortedData(sorted);
  };

  // Statistics Calculation
  const getStatistics = () => {
    let totalDemand = 0,
      totalMet = 0,
      totalNeedToGet = 0,
      totalCrops = sortedData.length;

    sortedData.forEach((cropData) => {
      totalDemand += cropData.demand;
      totalMet += cropData.met;
      totalNeedToGet += cropData.demand - cropData.met;
    });

    return {
      averageDemand: (totalDemand / totalCrops).toFixed(2),
      averageMet: (totalMet / totalCrops).toFixed(2),
      averageDemandMetPercentage: ((totalMet / totalDemand) * 100).toFixed(2),
      totalNeedToGet: totalNeedToGet.toFixed(2),
    };
  };

  // Loading and error state
  if (loading) <loading />;

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  const statistics = getStatistics();

  // Bar chart data for Demand vs Met
  const chartDataDemandMet = {
    labels: sortedData.map((cropData) => cropData.crop),
    datasets: [
      {
        label: 'Demand',
        data: sortedData.map((cropData) => cropData.demand),
        backgroundColor: '#f39c12', // Orange for demand
        borderRadius: 4,
      },
      {
        label: 'Met',
        data: sortedData.map((cropData) => cropData.met),
        backgroundColor: '#27ae60', // Green for met
        borderRadius: 4,
      },
    ],
  };

  const handleSignIn = async () => {
    try {
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Sign-out error: ", err.message);
    }
  };


  return (
    <>
      <div style={styles.main_container}>
        <div style={styles.main_header}>
          <span style={styles.main_welcomeMessage}>
            Welcome Guest
          </span>

          <div
            style={styles.profileImageWrapper}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            ref={dropdownRef}
          >
            <img
              src="https://matops-wave.firebaseapp.com/logo_tab.ico"
              alt="Sign In"
              style={styles.main_profileImage}
            />
            {isMenuOpen && (
              <div style={styles.main_dropdown}>
                <div
                  style={{
                    ...styles.main_dropdownItem,
                    ...(hoveredItem === "LogIn"
                      ? styles.main_dropdownItemHover
                      : {}),
                  }}
                  onMouseEnter={() => setHoveredItem("LogIn")}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={handleSignIn}
                >
                  Sign In
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    
      <div style={styles.main_mainContent}>
        <div style={styles.container}>

          <h2 style={styles.header}>Crop Estimation Data</h2>

          {/* Key Statistics */}
          <div style={styles.statsContainer}>
            <StatCard title="Average Demand" value={statistics.averageDemand} />
            <StatCard title="Average Met" value={statistics.averageMet} />
            <StatCard title="Demand Met %" value={`${statistics.averageDemandMetPercentage}%`} />
            <StatCard title="Total Need to Get" value={statistics.totalNeedToGet} />
          </div>

          {/* Charts */}
          <div style={styles.chartSection}>
            <div style={styles.chartWrapper}>
              <Bar
                data={chartDataDemandMet}
                options={{
                  responsive: true,
                  plugins: { title: { display: true, text: 'Demand vs Met for Each Crop' } },
                  scales: { x: { ticks: { autoSkip: true, maxRotation: 90, minRotation: 90 } } },
                }}
              />
            </div>
          </div>

          {/* Sort Button */}
          <div style={styles.sortButtonWrapper}>
            <button onClick={handleSort} style={styles.button}>
              Sort by Demand Met {sortOrder === 'asc' ? '(Ascending)' : '(Descending)'}
            </button>
          </div>

          {/* Crop Table */}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Crop</th>
                  <th style={styles.th}>Demand</th>
                  <th style={styles.th}>Met</th>
                  <th style={styles.th}>Demand Met %</th>
                  <th style={styles.th}>Need to Get</th>
                  <th style={styles.th}>Demand Met Breakdown</th> {/* Title for Pie chart column */}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((cropData, index) => (
                  <CropTableRow cropData={cropData} index={index} key={cropData.crop} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </>

  );
};

// Inline styles as objects
const styles = {

  main_container: {
    width: "100vw",
    fontFamily: "'Poppins', sans-serif",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  main_header: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    color: "white",
    padding: "10px 20px",
    fontSize: "18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 1000,
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(5px)",
  },

  main_welcomeMessage: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "#fff",
    marginRight: "20px",
  },

  main_profileImage: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    border: "2px solid #fff",
  },

  main_dropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
    padding: "10px 0",
    zIndex: 1001,
  },
  main_dropdownItem: {
    padding: "10px 20px",
    color: "#333",
    textDecoration: "none",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background-color 0.3s",
  },
  main_dropdownItemHover: {
    backgroundColor: "#f0f0f0",
  },

  main_mainContent: {
    flex: 1,
    marginTop: "60px", // Adjust to leave space for the fixed header
    overflowY: "auto", // Enable vertical scrolling
    padding: "20px",
  },

  container: {
    margin: '40px auto', // Apply 30px top and bottom margin, auto for horizontal centering
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '90%',
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent background
    border: '2px solid rgba(255, 255, 255, 0.9)', // Border
  },


  header: {
    fontSize: '24px',
    fontWeight: '600',
    padding: '0px 0px 10px',
    textAlign: 'center',
    color: '#ffff',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginBottom: '20px',
    gap: '20px',
  },
  statCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent background
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.4)',
    textAlign: 'center',
    width: '180px',
  },
  statTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#555',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2d3d4c',
  },
  chartSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  chartWrapper: {
    width: '100%',
    maxWidth: '800px',
    marginBottom: '20px',
    backgroundColor: '#f0f0f0',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.4)',
  },  
  pieChartWrapper: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
  },
  sortButtonWrapper: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  button: {
    padding: '10px 18px',
    fontSize: '14px',
    backgroundColor: '#4CAF50', // Solid button color
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },

  loginButtonContainer: {
    textAlign: 'center',
    marginTop: '20px',
  },

  tableWrapper: {
    marginTop: '30px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '10px',
    borderRadius: '8px',
    overflow: 'hidden',
    tableLayout: 'fixed', // Ensures columns are evenly spaced
  },
  
  th: {
    padding: '8px 16px',
    backgroundColor: '#4CAF50', // Solid background for header
    color: '#fff',
    textAlign: 'left',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    backdropFilter: 'blur(5px)', // Glass effect
    width: '16.66%', // Make each column take equal width (total 100%)
  },
  
  td: {
    padding: '8px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #ddd',
    fontSize: '14px',
    verticalAlign: 'middle',
    width: '16.66%', // Make each column take equal width (total 100%)
  },
  
  tableRowEven: {
    backgroundColor: '#f9f9f9',
  },
  tableRowOdd: {
    backgroundColor: '#ffffff',
  },
  loading: {
    fontSize: '16px',
    textAlign: 'center',
    marginTop: '20px',
  },
  error: {
    color: 'red',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: '20px',
  },
};

export default EstimationMeetComponent;
